<template>
  <AppContainer is-loading />
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import { useApiRequestHelper } from '@/composables/useApiRequestHelper'
import { useGetConfigVar } from '@/composables/useGetConfigVar.js'
import useDayJs from '@/composables/useDayJs.js'
import { useRedirectToError } from '@/composables/useRedirectToError'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { isLocationEnabled } from '@/composables/useVerificationMethod.js'
import { EngineerSessionLocationVerificationMethod } from '@/enums/EngineerSessionLocationVerificationMethod.js'
import { useAppStore } from '@/stores/AppStore'
import { useAuthStore } from '@/stores/authenticated/AuthStore'
import { resetAllStores } from '@/stores/resetAllStores'
import { inject, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const { t, locale } = useI18n()
const axios = inject('$axios')
const route = useRoute()
const router = useRouter()
const { apiUrlBase, setApiUrlAuthenticated } = useApiRequestHelper()

const appStore = useAppStore()
const authStore = useAuthStore()

const callerPath = 'views.login'

onMounted(async () => {
  resetAllStores()

  const loginToken = route.params.loginToken
  const apiUrl = route.query.api_url

  if (!loginToken || !apiUrl) {
    await useRedirectToError(router)
      .setTitle(t(useTranslationPath(callerPath, 'errorTitle')))
      .setMessage(t(useTranslationPath(callerPath, 'errorMessage')))
      .setStatus(400)
      .redirect()

    return
  }

  await appStore.$patch({
    apiUrl: apiUrl,
    showBackButton: false,
  })

  await setApiUrlAuthenticated()

  await axios
    .get(`${apiUrlBase.value}/login/${loginToken}`)
    .then(async ({ data: { data } }) => {
      await appStore.$patch({
        bearerToken: data.bearer_token,
      })

      await authStore.updateUser(
        data.user_id,
        data.user_language,
        data.user_locale,
        data.maintainable_entity_id,
        data.reduced_access,
      )

      useDayJs().locale(data.user_locale)

      if (locale.value !== data.user_language) {
        locale.value = data.user_language
      }

      authStore.$patch({ isAuthenticated: true })
    })
    .catch(async (error) => {
      await useRedirectToError(router)
        .setTitle(t(useTranslationPath(callerPath, 'errorTitle')))
        .setMessage(t(useTranslationPath(callerPath, 'errorMessage')))
        .setStatus(400)
        .redirect()
    })

  await appStore.getConfigVarsAuthenticated()

  if (
    useGetConfigVar('ENGINEER_APP_CHECK_IN_METHOD') ===
    EngineerSessionLocationVerificationMethod.ACCESS_CODE
  ) {
    await router.push({
      name: 'verification.code',
    })

    return
  }

  if (isLocationEnabled('ENGINEER_APP_CHECK_IN_METHOD')) {
    await router.push({
      name: 'verification.geo',
    })

    return
  }

  await router.push({ name: 'work-order.index' })
})
</script>
